import {
  Box,
  Button,
  CircularProgress,
  FormLabel,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import React from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CurrencyField } from '~/components/GenericForm/CurrencyField';
import { EnumSelect } from '~/components/GenericForm/EnumSelect';
import { BillableHoursProposalProps } from '~/data/proposals';
import { EditCustomDataFields } from '~/legacy-components/CustomDataFields/EditCustomDataFields';
import { BillableHoursTypeConfigSelector } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BillableHoursProposalForm/components/BillableHoursTypeConfigSelector';
import {
  CommonEmployeeProposalFormHandle,
  CommonEmployeeProposalFormProps,
  ProposalDataFormButtons,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalResponsibleHrSelect';
import { useProposalFormImperativeHandle } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalFormImperativeHandle';
import { BillableHoursProposalFormProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeBillableHoursProposalsLayout/useBillableHoursProposalsLayout';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

import { useSxStyles } from '../Common/useStyles';

import { useBillableHoursProposalForm } from './useBillableHoursProposalForm';

type PropsType = CommonEmployeeProposalFormProps & {
  proposal: BillableHoursProposalProps;
  formProps: BillableHoursProposalFormProps;
};

export const BillableHoursProposalForm = React.forwardRef<
  CommonEmployeeProposalFormHandle,
  PropsType
>((props, ref) => {
  const { proposal, employeeId, isNewProposal, onProposalUpdate, formProps } =
    props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    form,
    responsibleHRSelector,
    billableHoursTypeConfigSelector,
    enums,
    handleFormSubmit,
    handleFormReset,
    handleCalculateTotal,
    customFields,
    readOnlyMode,
    canSubmitForm,
    isLoading,
    fieldProps,
    currency,
  } = useBillableHoursProposalForm({
    proposal,
    employeeId,
    onProposalUpdate,
    formProps,
  });

  useProposalFormImperativeHandle({ ref, handleFormSubmit });

  const { control, formState } = form;

  if (isLoading.responsibleHR || isLoading.commonFieldProps) {
    return <CircularProgress />;
  }

  return (
    <Box autoComplete="off" component="form" sx={sxStyles.form}>
      <Controller
        name={'hoursType'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <BillableHoursTypeConfigSelector
              FormControlProps={{ fullWidth: true, error: !!error }}
              value={value}
              onHoursTypeChange={onChange}
              {...billableHoursTypeConfigSelector}
              id={'hoursType'}
              label={t('laBonusName')}
              required={true}
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'hours'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <TextField
              label={t('laHours')}
              value={value}
              onChange={onChange}
              fullWidth={true}
              required={true}
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 2,
        }}
      >
        <Controller
          name={'total'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <CurrencyField
                label={t('laTotal')}
                value={value}
                onChange={onChange}
                currency={currency}
                fullWidth={true}
                required={true}
                error={!!error?.message}
                helperText={error?.message}
                disabled={readOnlyMode}
              />
            );
          }}
        />
        <Button
          onClick={handleCalculateTotal}
          disabled={readOnlyMode || fieldProps.calculateTotal.disabled}
        >
          {t('laUseDefault')}
        </Button>
      </Box>

      <Controller
        name={'projectType'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true }}
              value={value}
              onChange={onChange}
              enumOptions={enums.billableProjects}
              id={'projectType'}
              label={t('laProjectType')}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      {customFields.map((customField, index) => (
        <EditCustomDataFields
          control={control as unknown as Control<FieldValues>}
          {...customField}
          key={`${index}/${customField.name}`}
        />
      ))}
      <Box sx={sxStyles.fieldSet}>
        <FormLabel>{t('laPayPeriod')}</FormLabel>
        <Box sx={sxStyles.dateRange}>
          <Controller
            name={'paymentPeriodStartDate'}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <DatePicker
                  label={t('laStartDate')}
                  {...convertDateTypeDatePickerProps({
                    value,
                    onChange,
                    ...fieldProps.datePickers.startDate,
                  })}
                  disabled={readOnlyMode}
                  slotProps={{
                    textField: {
                      required: true,
                      error: !!error?.message,
                      helperText: error?.message,
                    },
                  }}
                />
              );
            }}
          />
          <Box sx={{ pb: 3 }}>-</Box>
          <Controller
            name={'paymentPeriodEndDate'}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <DatePicker
                  label={t('laEndDate')}
                  {...convertDateTypeDatePickerProps({
                    value,
                    onChange,
                    ...fieldProps.datePickers.endDate,
                  })}
                  disabled={readOnlyMode}
                  slotProps={{
                    textField: {
                      error: !!error?.message,
                      helperText: error?.message,
                    },
                  }}
                />
              );
            }}
          />
        </Box>
      </Box>
      <Controller
        name={'paymentDate'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <DatePicker
              label={t('laBonusDate')}
              {...convertDateTypeDatePickerProps({
                value,
                onChange,
              })}
              minDate={moment()}
              slotProps={{
                textField: {
                  required: true,
                  error: !!error?.message,
                  helperText: error?.message,
                },
              }}
            />
          );
        }}
      />
      <Controller
        name={'info'}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              label={t('laInfo')}
              {...field}
              fullWidth={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <ProposalResponsibleHRSelect
        form={form}
        control={control}
        selectorSettings={responsibleHRSelector}
        disabled={readOnlyMode}
      />
      <ProposalDataFormButtons
        isNewProposal={isNewProposal}
        onFormSubmitWithDecision={handleFormSubmit}
        onFormReset={handleFormReset}
        formState={formState}
        readOnlyMode={readOnlyMode}
        submitDecisionAllowed={canSubmitForm}
      />
    </Box>
  );
});
